import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { modelsApi } from '../../api/models';
import { settingsApi } from '../../api/settings';
import Toast from './Toast';
import './ModelPicker.css';

/**
 * ModelPicker — the ONE reusable model picker used on every surface that
 * needs a per-function model choice (chat, card generation, Story Board
 * generation, prose generation) and on Settings "My Models". Replaces
 * ModelPulldown (a native <select>) — a searchable, price-filterable,
 * catalog-browsing panel. Do not fork copies of this component.
 *
 * Two modes:
 *   - "select" (the four generation surfaces): a compact trigger button
 *     opens a popover/modal panel; clicking a row saves the per-function
 *     preference and closes.
 *   - "curate" (Settings "My Models"): the same panel content renders
 *     inline, with checkboxes on every row instead of click-to-select.
 */

// ---------------------------------------------------------------------------
// Pure helpers (exported for direct unit testing)
// ---------------------------------------------------------------------------

const PRICE_BAND_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'budget', label: 'Budget' },
  { key: 'mid', label: 'Mid-range' },
  { key: 'premium', label: 'Premium' },
];

/**
 * Extracts the short relative-cost phrase from the end of a curated default
 * model's note (e.g. "Moderate cost", "Exceptional value", "Highest cost").
 * Splits on ". " to isolate the last sentence, strips the trailing period,
 * then — for notes whose final sentence itself contains an em dash aside
 * ("Highest cost — best saved for the scenes that matter most.") — keeps
 * only the clause before the dash. Returns null for notes with no content
 * (added/catalog models have note === null).
 */
export function extractCostTag(note) {
  if (!note || typeof note !== 'string') return null;
  const sentences = note.split('. ').map((s) => s.trim()).filter(Boolean);
  if (sentences.length === 0) return null;
  let last = sentences[sentences.length - 1].replace(/\.$/, '');
  const dashIdx = last.indexOf(' — ');
  if (dashIdx !== -1) {
    last = last.slice(0, dashIdx).trim();
  }
  return last || null;
}

/**
 * Sorts catalog entries with a parseable `pricing.completion` ascending and
 * cuts them into three equal-count bands (budget / mid / premium) at the
 * one-third and two-thirds index marks. Entries with a missing/unparseable
 * price are excluded from the map entirely (callers treat "not in the map"
 * as "only visible under All").
 * @returns {Map<string, 'budget'|'mid'|'premium'>}
 */
export function computePriceBandMap(catalogModels) {
  const priced = (catalogModels || [])
    .map((m) => {
      const raw = m?.pricing?.completion;
      // Number(null) is 0 and Number('') is 0 — both would masquerade as a
      // real (and the cheapest possible) price, so treat "no value" as NaN
      // explicitly rather than relying on Number()'s coercion.
      const price = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
      return { id: m.id, price };
    })
    .filter((m) => Number.isFinite(m.price));
  priced.sort((a, b) => a.price - b.price);

  const n = priced.length;
  const cut1 = Math.floor(n / 3);
  const cut2 = Math.floor((2 * n) / 3);
  const map = new Map();
  priced.forEach((m, i) => {
    const band = i < cut1 ? 'budget' : i < cut2 ? 'mid' : 'premium';
    map.set(m.id, band);
  });
  return map;
}

/** Case-insensitive substring match against whichever fields are present. */
export function matchesSearch(fields, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  return fields.filter(Boolean).some((f) => String(f).toLowerCase().includes(q));
}

function formatRate(rate) {
  if (rate === null || rate === undefined || rate === '') return '—';
  const num = Number(rate);
  if (Number.isNaN(num)) return '—';
  return `$${(num * 1_000_000).toFixed(2)}`;
}

/** "$2.00 / $10.00" — compact in-row form (model-picker-polish F2; the fully
 * labeled form moved to the detail strip, see formatLabeledPrice). */
function formatCompactPrice(pricing) {
  if (!pricing) return null;
  return `${formatRate(pricing.prompt)} / ${formatRate(pricing.completion)}`;
}

/** "$2.00 in / $10.00 out per 1M tokens" — the detail strip's fully labeled form. */
function formatLabeledPrice(pricing) {
  if (!pricing) return null;
  return `${formatRate(pricing.prompt)} in / ${formatRate(pricing.completion)} out per 1M tokens`;
}

/**
 * "1M-token context" / "200K-token context" / "512-token context" — a plain,
 * rounded context-size phrase for the detail strip (model-picker-polish F5).
 * Returns null for a missing/non-positive/unparseable value.
 */
export function formatContextPhrase(contextLength) {
  const n = Number(contextLength);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1_000_000) return `${Math.round(n / 1_000_000)}M-token context`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K-token context`;
  return `${n}-token context`;
}

function titleCaseMaker(maker) {
  if (!maker) return 'Other';
  return maker
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Clamps the popover's fixed position so it stays fully within the viewport
 * with `margin` on every side (model-picker-polish F1). Anchors below the
 * trigger by default; flips above when there isn't room below (and there's
 * more room above than below). `maxHeight` caps the panel to whichever side
 * it ends up on, so a panel taller than the available space still fits
 * rather than running off the opposite edge.
 * @param {{top:number,bottom:number,left:number,right:number}} triggerRect
 * @param {{width:number,height:number}} panelSize
 * @param {{width:number,height:number}} viewportSize
 * @param {number} [margin=12]
 * @returns {{top:number,left:number,maxHeight:number}}
 */
export function computePanelPosition(triggerRect, panelSize, viewportSize, margin = 12) {
  const panelWidth = panelSize.width;
  const panelHeight = panelSize.height;
  const viewportWidth = viewportSize.width;
  const viewportHeight = viewportSize.height;

  let left = triggerRect.left;
  const maxLeft = viewportWidth - panelWidth - margin;
  if (left > maxLeft) left = maxLeft;
  if (left < margin) left = margin;

  const spaceBelow = viewportHeight - triggerRect.bottom - margin;
  const spaceAbove = triggerRect.top - margin;

  let top;
  let maxHeight;
  if (spaceBelow >= panelHeight || spaceBelow >= spaceAbove) {
    // Preferred: open below. Cap height to whatever room is actually there.
    top = triggerRect.bottom + 6;
    maxHeight = Math.max(0, Math.min(panelHeight, viewportHeight - top - margin));
  } else {
    // Not enough room below, and there's more room above — flip up.
    maxHeight = Math.max(0, Math.min(panelHeight, spaceAbove));
    top = Math.max(margin, triggerRect.top - 6 - maxHeight);
  }

  return { top, left, maxHeight };
}

// ---------------------------------------------------------------------------
// Shared catalog fetch — one request per page load regardless of how many
// pickers open, since the server already caches it ~1h. Module-level so it
// survives across component instances/unmounts.
// ---------------------------------------------------------------------------

let catalogPromise = null;

/** Test-only escape hatch — resets the shared cache between test cases. */
export function resetModelCatalogCacheForTests() {
  catalogPromise = null;
}

function loadCatalogOnce() {
  if (!catalogPromise) {
    catalogPromise = settingsApi
      .getOpenRouterCatalog()
      .then((res) => res?.data?.models || res?.models || [])
      .catch((err) => {
        console.error('Failed to load OpenRouter catalog:', err);
        return [];
      });
  }
  return catalogPromise;
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function PickerRow({
  name,
  priceLine,
  costTag,
  mode,
  checked,
  isSelected,
  disabled,
  showAddButton,
  onPrimaryAction,
  onCheckboxChange,
  onActivate,
  detail,
  ariaLabel,
}) {
  const activate = () => onActivate?.(detail ?? null);
  const className = [
    'model-picker__row',
    isSelected && 'model-picker__row--selected',
  ].filter(Boolean).join(' ');

  if (mode === 'curate') {
    return (
      <label className={className} onMouseEnter={activate} onFocus={activate} onTouchStart={activate}>
        <input
          type="checkbox"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onCheckboxChange(e.target.checked)}
        />
        <span className="model-picker__row-name">{name}</span>
        <span className="model-picker__row-price">{priceLine || ''}</span>
        <span className="model-picker__row-tag">{costTag || ''}</span>
      </label>
    );
  }

  if (showAddButton) {
    return (
      <div className={className} onMouseEnter={activate} onFocus={activate} onTouchStart={activate}>
        <span className="model-picker__row-name">{name}</span>
        <span className="model-picker__row-price">{priceLine || ''}</span>
        <button
          type="button"
          className="model-picker__add-btn"
          disabled={disabled}
          onClick={onPrimaryAction}
          aria-label={`Add ${name} and use it here`}
        >
          + Add
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${className} model-picker__row--button`}
      disabled={disabled}
      onClick={onPrimaryAction}
      onMouseEnter={activate}
      onFocus={activate}
      onTouchStart={activate}
      aria-pressed={isSelected}
      aria-label={ariaLabel}
    >
      <span className="model-picker__row-name">{name}</span>
      <span className="model-picker__row-price">{priceLine || ''}</span>
      <span className="model-picker__row-tag">{costTag || ''}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// ModelPicker
// ---------------------------------------------------------------------------

/**
 * Marks the portaled select-mode panel. Parents that close on a document
 * mousedown (the panel is rendered on document.body, outside their ref)
 * can ignore events inside this node so a row click still lands.
 */
export const MODEL_PICKER_PANEL_SELECTOR = '[data-model-picker-panel]';

export function isEventInsideModelPickerPanel(event) {
  const target = event?.target;
  return Boolean(target?.closest?.(MODEL_PICKER_PANEL_SELECTOR));
}

export function isModelPickerPanelOpen() {
  return Boolean(document.querySelector(MODEL_PICKER_PANEL_SELECTOR));
}

const ModelPicker = ({ mode, functionKey, onChange, onReady, disabled }) => {
  const [menu, setMenu] = useState({ default: [], added: [] });
  const [settings, setSettings] = useState(null);
  const [catalog, setCatalog] = useState(null); // null = not yet loaded
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [band, setBand] = useState('all');
  const [expandedMakers, setExpandedMakers] = useState(() => new Set());
  const [activeDetail, setActiveDetail] = useState(undefined); // undefined = nothing active -> fall back
  const [panelStyle, setPanelStyle] = useState({});

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);

  // Load menu + settings on mount (both modes) — select mode's trigger has
  // to show the writer's current model right away, before the panel is ever
  // opened, the same way ModelPulldown always did.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [menuRes, settingsRes] = await Promise.all([
          modelsApi.getAvailable(),
          settingsApi.getSettings(),
        ]);
        if (cancelled) return;
        const models = menuRes?.models || { default: [], added: [] };
        const settingsData = settingsRes?.data || settingsRes || {};
        setMenu(models);
        setSettings(settingsData);
        if (mode === 'select') {
          setSelected(settingsData?.model_preferences?.[functionKey] || '');
        }
      } catch (err) {
        console.error(`Failed to load model picker data for ${functionKey || 'curate'}:`, err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, functionKey]);

  useEffect(() => {
    if (mode !== 'select' || loading) return;
    onReady?.();
  }, [mode, loading, onReady]);

  // Catalog: shared across every picker on the page, fetched once settings
  // resolve and only if there's an API key (the endpoint has always required
  // one — the same gate the old My Models search had).
  useEffect(() => {
    if (!settings?.has_api_key) return undefined;
    let cancelled = false;
    loadCatalogOnce().then((models) => {
      if (!cancelled) setCatalog(models);
    });
    return () => { cancelled = true; };
  }, [settings?.has_api_key]);

  const closePanel = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Escape + outside click (select mode only)
  useEffect(() => {
    if (mode !== 'select' || !open) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    const handlePointerDown = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        closePanel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [mode, open, closePanel]);

  // Focus the search field on open, and trap Tab inside the panel.
  useEffect(() => {
    if (mode !== 'select' || !open) return undefined;
    searchInputRef.current?.focus();
    const handleTabKey = (e) => {
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = Array.from(
        panelRef.current.querySelectorAll(
          'button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [mode, open]);

  // Anchor the popover under the trigger on desktop, clamped to stay fully
  // within the viewport (model-picker-polish F1) — recomputed on resize
  // while open. The mobile media query overrides all of this with a
  // full-screen fixed layout.
  useLayoutEffect(() => {
    if (mode !== 'select' || !open) return undefined;
    const reposition = () => {
      if (!triggerRef.current || !panelRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const panelRect = panelRef.current.getBoundingClientRect();
      const pos = computePanelPosition(
        triggerRect,
        { width: panelRect.width, height: panelRect.height },
        { width: window.innerWidth, height: window.innerHeight }
      );
      setPanelStyle({ position: 'fixed', top: pos.top, left: pos.left, maxHeight: pos.maxHeight });
    };
    reposition();
    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, [mode, open]);

  // Reset transient UI state each time the panel closes so it reopens fresh.
  useEffect(() => {
    if (mode === 'select' && !open) {
      setSearch('');
      setBand('all');
      setActiveDetail(undefined);
    }
  }, [mode, open]);

  const allKnown = useMemo(() => [...menu.default, ...menu.added], [menu]);
  const isInMenu = useCallback(
    (id) => menu.default.some((m) => m.id === id) || menu.added.some((m) => m.id === id),
    [menu]
  );

  const catalogById = useMemo(() => {
    const map = new Map();
    (catalog || []).forEach((m) => map.set(m.id, m));
    return map;
  }, [catalog]);

  const bandById = useMemo(() => computePriceBandMap(catalog || []), [catalog]);

  const costTagByDefaultId = useMemo(() => {
    const map = new Map();
    menu.default.forEach((m) => map.set(m.id, extractCostTag(m.note)));
    return map;
  }, [menu.default]);

  // Saved choice may no longer be in either menu group (hidden after being
  // saved elsewhere) — still show it, pinned at the top, selectable.
  const isHidden = mode === 'select' && !!selected && !allKnown.some((m) => m.id === selected);
  const hiddenModel = isHidden
    ? { id: selected, display_name: catalogById.get(selected)?.name || selected }
    : null;

  const matchesBand = useCallback(
    (id) => {
      if (band === 'all') return true;
      return bandById.get(id) === band;
    },
    [band, bandById]
  );

  const visibleDefaults = menu.default.filter(
    (m) => matchesSearch([m.display_name, m.id], search) && matchesBand(m.id)
  );
  const visibleAdded = menu.added.filter(
    (m) => matchesSearch([m.display_name, m.id], search) && matchesBand(m.id)
  );
  const noMenuRowsVisible = !isHidden && visibleDefaults.length === 0 && visibleAdded.length === 0;

  const filteredCatalog = useMemo(
    () =>
      (catalog || []).filter(
        (m) => matchesSearch([m.name, m.id, m.maker], search) && matchesBand(m.id)
      ),
    [catalog, search, matchesBand]
  );

  const makerGroups = useMemo(() => {
    const groups = new Map();
    filteredCatalog.forEach((m) => {
      const key = m.maker || 'other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m);
    });
    return Array.from(groups.entries())
      .map(([maker, models]) => ({ maker, label: titleCaseMaker(maker), models, count: models.length }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredCatalog]);

  const toggleMaker = (maker) => {
    setExpandedMakers((prev) => {
      const next = new Set(prev);
      if (next.has(maker)) next.delete(maker);
      else next.add(maker);
      return next;
    });
  };

  // A non-empty search auto-shows every matching maker's rows (spec F3) —
  // pills still render (with filtered counts) and stay independently
  // toggleable underneath, but while searching, "matches" already implies
  // "shown," no tap required.
  const searchActive = search.trim().length > 0;
  const effectiveExpandedMakers = searchActive
    ? new Set(makerGroups.map((g) => g.maker))
    : expandedMakers;

  const selectedInMenu = allKnown.find((m) => m.id === selected);

  // -- select-mode actions ---------------------------------------------

  const handleSelectRow = async (modelId, modelObj) => {
    const previous = selected;
    setSelected(modelId); // optimistic
    setPendingId(modelId);
    try {
      await settingsApi.updateModels({ [functionKey]: modelId });
      setToast({ message: 'Model preference saved', type: 'success' });
      onChange?.(modelId, modelObj || null);
      closePanel();
    } catch (err) {
      console.error(`Failed to save model preference for ${functionKey}:`, err);
      setSelected(previous);
      setToast({ message: 'Failed to save model preference', type: 'error' });
    } finally {
      setPendingId(null);
    }
  };

  const handleAddAndSelect = async (catalogModel) => {
    setPendingId(catalogModel.id);
    try {
      const addRes = await settingsApi.addToModelMenu(catalogModel.id);
      const models = addRes?.data?.models || addRes?.models;
      if (models) setMenu(models);
    } catch (err) {
      console.error(`Failed to add ${catalogModel.id} to model menu:`, err);
      setToast({ message: 'Failed to add model to your menu', type: 'error' });
      setPendingId(null);
      return;
    }

    const previous = selected;
    setSelected(catalogModel.id);
    try {
      await settingsApi.updateModels({ [functionKey]: catalogModel.id });
      setToast({ message: 'Model preference saved', type: 'success' });
      onChange?.(catalogModel.id, { id: catalogModel.id, display_name: catalogModel.name, note: null });
      closePanel();
    } catch (err) {
      console.error(`Failed to save model preference for ${functionKey}:`, err);
      setSelected(previous);
      setToast({ message: 'Failed to save model preference', type: 'error' });
    } finally {
      setPendingId(null);
    }
  };

  // -- curate-mode actions -----------------------------------------------

  const handleToggleCurate = async (modelId, shouldAdd) => {
    setPendingId(modelId);
    try {
      const res = shouldAdd
        ? await settingsApi.addToModelMenu(modelId)
        : await settingsApi.hideFromModelMenu(modelId);
      const models = res?.data?.models || res?.models;
      if (models) setMenu(models);
      setToast({
        message: shouldAdd ? 'Model added to your menu' : 'Model removed from your menu',
        type: 'success',
      });
    } catch (err) {
      console.error(`Failed to ${shouldAdd ? 'add' : 'remove'} ${modelId}:`, err);
      setToast({
        message: shouldAdd ? 'Failed to add model to your menu' : 'Failed to remove model from your menu',
        type: 'error',
      });
    } finally {
      setPendingId(null);
    }
  };

  // -- derived display ------------------------------------------------

  const triggerLabel = loading && !selected
    ? 'Loading…'
    : hiddenModel
      ? `${hiddenModel.display_name} (hidden from your list)`
      : selectedInMenu?.display_name || (selected || 'Select a model…');

  // Every row's detail is {text, pricing, contextLength} — for a default,
  // text is the curated note (unchanged); for an added/catalog/hidden row,
  // text is OpenRouter's own description (from the catalog join), which may
  // be null. pricing/contextLength always come from the catalog join so the
  // detail strip's labeled price + context phrase work for every row kind
  // (model-picker-polish F5).
  const detailFor = (modelId, curatedNote) => {
    const catalogEntry = catalogById.get(modelId);
    return {
      text: curatedNote ?? catalogEntry?.description ?? null,
      pricing: catalogEntry?.pricing || null,
      contextLength: catalogEntry?.context_length ?? null,
    };
  };

  const fallbackDetail = mode === 'select' && selectedInMenu
    ? detailFor(selectedInMenu.id, selectedInMenu.note)
    : null;
  const detail = activeDetail !== undefined ? activeDetail : fallbackDetail;
  const detailPriceLine = detail ? formatLabeledPrice(detail.pricing) : null;
  const detailContextPhrase = detail ? formatContextPhrase(detail.contextLength) : null;
  const hasDetailContent = !!(detail && (detail.text || detailPriceLine || detailContextPhrase));

  const catalogGated = !settings?.has_api_key;

  const panelBody = (
    <>
      <div className="model-picker__search-row">
        <input
          ref={searchInputRef}
          type="text"
          className="model-picker__search"
          placeholder="Search models by name, id, or maker…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search models"
        />
      </div>

      <div className="model-picker__chips-row" role="group" aria-label="Filter by price">
        {PRICE_BAND_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type="button"
            className={`model-picker__chip ${band === opt.key ? 'model-picker__chip--active' : ''}`}
            onClick={() => setBand(opt.key)}
            aria-pressed={band === opt.key}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="model-picker__scroll" onMouseLeave={() => setActiveDetail(undefined)}>
        <div className="model-picker__menu-section">
          {isHidden && hiddenModel && (
            <PickerRow
              name={`${hiddenModel.display_name} (hidden from your list)`}
              priceLine={formatCompactPrice(catalogById.get(hiddenModel.id)?.pricing)}
              costTag={null}
              mode={mode}
              checked={mode === 'curate'}
              isSelected={mode === 'select' && selected === hiddenModel.id}
              disabled={disabled || pendingId === hiddenModel.id}
              showAddButton={false}
              onPrimaryAction={() => handleSelectRow(hiddenModel.id, null)}
              onCheckboxChange={() => handleSelectRow(hiddenModel.id, null)}
              onActivate={setActiveDetail}
              detail={detailFor(hiddenModel.id, null)}
              ariaLabel={`${hiddenModel.display_name}, hidden from your list`}
            />
          )}

          {visibleDefaults.map((model) => (
            <PickerRow
              key={model.id}
              name={model.display_name}
              priceLine={formatCompactPrice(catalogById.get(model.id)?.pricing)}
              costTag={costTagByDefaultId.get(model.id)}
              mode={mode}
              checked
              isSelected={mode === 'select' && selected === model.id}
              disabled={disabled || pendingId === model.id}
              showAddButton={false}
              onPrimaryAction={() => handleSelectRow(model.id, model)}
              onCheckboxChange={(checked) => handleToggleCurate(model.id, checked)}
              onActivate={setActiveDetail}
              detail={detailFor(model.id, model.note)}
              ariaLabel={model.display_name}
            />
          ))}

          {visibleAdded.length > 0 && (
            <>
              <div className="model-picker__subhead">Your models</div>
              {visibleAdded.map((model) => (
                <PickerRow
                  key={model.id}
                  name={model.display_name}
                  priceLine={formatCompactPrice(catalogById.get(model.id)?.pricing)}
                  costTag={null}
                  mode={mode}
                  checked
                  isSelected={mode === 'select' && selected === model.id}
                  disabled={disabled || pendingId === model.id}
                  showAddButton={false}
                  onPrimaryAction={() => handleSelectRow(model.id, model)}
                  onCheckboxChange={(checked) => handleToggleCurate(model.id, checked)}
                  onActivate={setActiveDetail}
                  detail={detailFor(model.id, null)}
                  ariaLabel={model.display_name}
                />
              ))}
            </>
          )}

          {noMenuRowsVisible && (
            <p className="model-picker__empty">No models match your search.</p>
          )}
        </div>

        <div className="model-picker__divider" />

        <div className="model-picker__catalog-section">
          <h4 className="model-picker__catalog-heading">Browse the catalog</h4>
          {catalogGated ? (
            <p className="model-picker__catalog-gate">Add an API key above to search the live catalog.</p>
          ) : catalog === null ? (
            <p className="model-picker__catalog-status">Loading catalog…</p>
          ) : makerGroups.length === 0 ? (
            <p className="model-picker__catalog-status">No models found.</p>
          ) : (
            <>
              {/* Maker pill grid (model-picker-polish F3) — a wrapped grid of
                  compact pills, several per line, replacing the old one-per-
                  line maker headers. Tapping toggles that maker's rows below;
                  multiple makers can be open at once. */}
              <div className="model-picker__maker-grid" role="group" aria-label="Filter by maker">
                {makerGroups.map((group) => {
                  const expanded = effectiveExpandedMakers.has(group.maker);
                  return (
                    <button
                      key={group.maker}
                      type="button"
                      className={`model-picker__maker-pill ${expanded ? 'model-picker__maker-pill--active' : ''}`}
                      onClick={() => toggleMaker(group.maker)}
                      aria-pressed={expanded}
                    >
                      {group.label} · {group.count}
                    </button>
                  );
                })}
              </div>

              {makerGroups
                .filter((group) => effectiveExpandedMakers.has(group.maker))
                .map((group) => (
                  <div key={group.maker} className="model-picker__maker-rows">
                    <div className="model-picker__maker-rows-heading">{group.label}</div>
                    {group.models.map((m) => (
                      <PickerRow
                        key={m.id}
                        name={m.name}
                        priceLine={formatCompactPrice(m.pricing)}
                        costTag={null}
                        mode={mode}
                        checked={isInMenu(m.id)}
                        isSelected={mode === 'select' && selected === m.id}
                        disabled={disabled || pendingId === m.id}
                        showAddButton={mode === 'select'}
                        onPrimaryAction={() => handleAddAndSelect(m)}
                        onCheckboxChange={(checked) => handleToggleCurate(m.id, checked)}
                        onActivate={setActiveDetail}
                        detail={{ text: m.description ?? null, pricing: m.pricing, contextLength: m.context_length }}
                        ariaLabel={m.name}
                      />
                    ))}
                  </div>
                ))}
            </>
          )}
        </div>
      </div>

      <div className="model-picker__detail-strip">
        {hasDetailContent && (
          <>
            {detail.text && <p className="model-picker__detail-note">{detail.text}</p>}
            {(detailPriceLine || detailContextPhrase) && (
              <p className="model-picker__detail-meta">
                {[detailPriceLine, detailContextPhrase].filter(Boolean).join(' · ')}
              </p>
            )}
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      {mode === 'select' ? (
        <div className="model-picker">
          <button
            ref={triggerRef}
            type="button"
            className="model-picker__trigger"
            onClick={() => (open ? closePanel() : setOpen(true))}
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <span className="model-picker__trigger-label">{triggerLabel}</span>
            <span className="model-picker__trigger-chevron" aria-hidden="true">▾</span>
          </button>
          {open &&
            createPortal(
              <div
                ref={panelRef}
                className="model-picker__panel"
                data-model-picker-panel=""
                style={panelStyle}
                role="dialog"
                aria-modal="true"
                aria-label="Choose an AI model"
              >
                {panelBody}
              </div>,
              document.body
            )}
        </div>
      ) : (
        <div className="model-picker model-picker--curate">{panelBody}</div>
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </>
  );
};

export default ModelPicker;
